// Lesson picker dialog: one card per lesson of the current language, showing the
// lesson's journey symbol — the same art that sits at the end of its progress bar
// (ABC Star for reading, Unicorn Star for fables, the earth planet for Daily Life,
// and the themed reward stars for the JA/KO/TH arcade lessons).
import i18next from 'i18next';
import { openChoicePicker } from './choicePicker';
import { packKey } from '@/game/packs';
import { isGrowingPlanetPool } from '@/data/pools';
import { lessonProgress } from '@/game/progression/lessonStats';
import { STAR_COUNT } from '@/game/progression/config';
import { starGlyphsFor } from '@/game/starGlyphs';
import type { Pack } from '@/data/packs/types';

// The Daily Life journey has no reward image (its planet is drawn procedurally in-game),
// so the dialog shows a static earth icon instead; lessons without any reward art get
// the default ABC Star, mirroring the progress bar's fallback.
//
// Exported because the Scoreboard panel heads its card with the same symbol — the art at the end
// of the progress bar, which is also the thing the child tapped to open it (specs/scoreboard.md §2).
export function lessonImage(pack: Pack): string {
  if (isGrowingPlanetPool(pack)) return '/images/earth-icon.svg';
  return pack.reward?.image || '/images/abc-star.png';
}

export function openLessonPicker(lessons: Pack[], current: Pack, onSelect: (pack: Pack) => void): void {
  openChoicePicker(
    i18next.t('menu.packChoose'),
    lessons.map((p) => {
      // Journey state on the card, so a finished lesson doesn't look identical to one never
      // opened. Deliberately a glyph and two numbers rather than a sentence — it needs no
      // translation and fits a card in every one of the ten languages.
      const progress = lessonProgress(p);
      return {
        id: packKey(p),
        tileHtml: `<span class="picker__icon"><img src="${lessonImage(p)}" alt="" draggable="false"></span>`,
        name: p.title,
        badge: `${starGlyphsFor(p).full} ${progress.stars}/${STAR_COUNT}`,
        done: progress.done,
        age: p.minAge,
      };
    }),
    packKey(current),
    (id) => {
      const pack = lessons.find((p) => packKey(p) === id);
      if (pack) onSelect(pack);
    },
  );
}
